import axios from 'axios'

import cookieUtility from '../utils/cookie.utility.js'
import authUtility from '../utils/auth.utility.js'
import httpUtility from '../utils/http.utility.js'

import { useAuthStore } from '../stores/authStore.js'
import { useToast } from '../composables/useToast.js'

import { CSRF_METHODS } from '../config/csrf.config.js'
import { ROUTE_PATHS } from '../config/route.config.js'

import { t } from '@/i18n'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''
const AUTH_URL = import.meta.env.VITE_AUTH_URL || ''

let contextMismatchHandled = false
let sessionExpiredHandled = false
let maintenanceHandled = false
/** One in-flight DAL refresh — concurrent 419s share it to avoid rotation races. */
let refreshPromise = null

function isContextMismatchError(error) {
  const message = error.response?.data?.message || ''
  return error.response?.status === 409 && message.startsWith('CONTEXT_MISMATCH')
}

function handleContextMismatch() {
  if (contextMismatchHandled) return
  contextMismatchHandled = true
  useToast().error(t('common.auth.errors.contextMismatch'))
  setTimeout(() => {
    window.location.reload()
  }, 1500)
}

async function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${AUTH_URL}/auth/refresh`, {}, { withCredentials: true })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

function handleSessionExpired() {
  if (sessionExpiredHandled) return
  sessionExpiredHandled = true
  useAuthStore().reset()
  authUtility.remove()
  useToast().error(t('common.auth.errors.sessionExpired'))
  const redirect = encodeURIComponent(`${window.location.pathname}${window.location.search}`)
  window.location.href = `${ROUTE_PATHS.login}?redirect=${redirect}`
}

/** Clear session and hard-nav to the downtime page (parity with legacy axios mixin). */
function handleMaintenance() {
  if (maintenanceHandled) return
  if (window.location.pathname === ROUTE_PATHS.maintenance) return
  maintenanceHandled = true
  useAuthStore().reset()
  authUtility.remove()
  window.location.href = ROUTE_PATHS.maintenance
}

const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  withCredentials: true,
  timeout: 10000,
})

apiClient.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore()
    const currentAccountId = authStore.user?.currentAccount?._id

    config.headers = config.headers || {}

    // Client view state on every API request — protect compares these to the
    // session (multi-tab: another tab may switch account or group mode).
    if (currentAccountId) {
      config.headers.currentAccount = String(currentAccountId)
      config.headers.isGroupMode = String(authStore.user?.groupMode ?? false)
    }

    if (CSRF_METHODS.includes(config.method?.toUpperCase())) {
      const csrfToken = cookieUtility.get(`${import.meta.env.VITE_COOKIE_PREFIX}csrf_token`)
      if (csrfToken) config.headers['X-CSRF-Token'] = csrfToken
    }
    return config
  },
  (error) => Promise.reject(error),
)

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isContextMismatchError(error)) {
      handleContextMismatch()
      return new Promise(() => {})
    }

    if (error.response?.status === 419) {
      const config = error.config
      if (!config || config._retry) {
        return Promise.reject(error)
      }
      config._retry = true

      try {
        await refreshSession()
      } catch (refreshError) {
        const status = refreshError.response?.status
        // Another tab may have refreshed first — cookies might already be valid.
        if (status === 401 && !config._refreshRaceRetry) {
          config._refreshRaceRetry = true
          return apiClient(config)
        }
        if (status === 401 || status === 403) {
          handleSessionExpired()
        }
        return Promise.reject(refreshError)
      }

      return apiClient(config)
    }

    if (
      error.request?.responseType === 'blob' &&
      error.response?.data instanceof Blob &&
      error.response.data.type.toLowerCase().includes('json')
    ) {
      error.response.data = JSON.parse(await error.response.data.text())
    } else if (error.request?.responseType === 'arraybuffer' && error.response?.data instanceof ArrayBuffer) {
      error.response.data = JSON.parse(new TextDecoder('utf-8').decode(error.response.data))
    }

    if (isContextMismatchError(error)) {
      handleContextMismatch()
      return new Promise(() => {})
    }

    if (httpUtility.error.isMaintenance(error)) {
      handleMaintenance()
      return new Promise(() => {})
    }

    return Promise.reject(error)
  },
)

export default apiClient
